import type { MetaFunction } from "@remix-run/cloudflare";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Separator } from "~/components/ui/separator";
import { Shield, Lock, Eye, Users, AlertCircle, FileText } from "lucide-react";

export const meta: MetaFunction = () => {
  return [
    { title: "Privacy Policy | Stellar Blade Mods" },
    { 
      name: "description", 
      content: "Learn how Stellar Blade Mods collects, uses, and protects your personal information. Our comprehensive privacy policy explains our data practices and your rights." 
    },
    { name: "keywords", content: "privacy policy, data protection, stellar blade mods, user privacy, personal information" },
    
    // Robots directive for legal pages
    { name: "robots", content: "index,follow" },
    
    // Canonical URL
    { tagName: "link", rel: "canonical", href: "https://stellarblademod.com/privacy-policy" },
    
    // Open Graph
    { property: "og:title", content: "Privacy Policy | Stellar Blade Mods" },
    { property: "og:description", content: "Learn how Stellar Blade Mods protects your privacy and handles your personal information." },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://stellarblademod.com/privacy-policy" },
    
    // Twitter Card
    { name: "twitter:card", content: "summary" },
    { name: "twitter:title", content: "Privacy Policy | Stellar Blade Mods" },
    { name: "twitter:description", content: "Learn how Stellar Blade Mods protects your privacy and handles your personal information." },
  ];
};

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <div className="relative text-white py-20 border-b border-slate-800 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-8">
              <div className="rounded-2xl bg-gradient-to-br from-green-500/20 to-blue-500/20 p-6 backdrop-blur-sm border border-white/10">
                <Shield className="h-16 w-16 text-green-400" />
              </div>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent">
              Privacy Policy
            </h1>
            
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed">
              We respect your privacy and are committed to protecting your personal information. 
              This policy explains how we collect, use, and safeguard your data.
            </p>
            
            <div className="flex items-center justify-center gap-6 text-sm text-slate-400">
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4" />
                <span>Your Data is Protected</span>
              </div>
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4" />
                <span>Transparent Practices</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container py-16">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8 p-4 bg-blue-900/20 border border-blue-700/30 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-400 mt-0.5" />
              <div>
                <p className="text-sm text-blue-300 font-medium mb-1">Last Updated</p>
                <p className="text-sm text-slate-300">
                  This Privacy Policy was last updated on {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            {/* Introduction */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <FileText className="h-6 w-6 text-blue-400" />
                  Introduction
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <p>
                  This Privacy Policy governs the manner in which Stellar Blade Mods ("we", "us", or "our") 
                  collects, uses, maintains and discloses information collected from users (each, a "User") 
                  of the stellarblademod.com website ("Site").
                </p>
                <p>
                  We are committed to ensuring that your privacy is protected. Should we ask you to provide 
                  certain information by which you can be identified when using this website, you can be 
                  assured that it will only be used in accordance with this privacy statement.
                </p>
              </CardContent>
            </Card>

            {/* Personal Information */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Users className="h-6 w-6 text-green-400" />
                  Personal Information Collection
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <h3 className="text-lg font-semibold text-white">Personal Identification Information</h3>
                <p>
                  We may collect personal identification information from Users in a variety of ways, including, 
                  but not limited to, when Users visit our site, register on the site, subscribe to newsletters, 
                  and in connection with other activities, services, features or resources we make available on our Site.
                </p>
                <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-600">
                  <p className="text-sm text-slate-400 mb-2">Information we may collect includes:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Email address</li>
                    <li>Username and display name</li>
                    <li>Profile information (when using OAuth providers like Discord, Google)</li>
                    <li>User preferences and settings</li>
                  </ul>
                </div>
                <p>
                  Users may visit our Site anonymously. We will collect personal identification information 
                  from Users only if they voluntarily submit such information to us. Users can always refuse 
                  to supply personal identification information, except that it may prevent them from engaging 
                  in certain Site related activities.
                </p>

                <Separator className="bg-slate-600" />

                <h3 className="text-lg font-semibold text-white">Non-Personal Identification Information</h3>
                <p>
                  We may collect non-personal identification information about Users whenever they interact 
                  with our Site. Non-personal identification information may include the browser name, the 
                  type of computer and technical information about Users' means of connection to our Site, 
                  such as the operating system and the Internet service providers utilized and other similar information.
                </p>
              </CardContent>
            </Card>

            {/* Cookies */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Lock className="h-6 w-6 text-yellow-400" />
                  Cookies and Tracking
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <h3 className="text-lg font-semibold text-white">Web Browser Cookies</h3>
                <p>
                  Our Site may use "cookies" to enhance User experience. User's web browser places cookies 
                  on their hard drive for record-keeping purposes and sometimes to track information about them. 
                  User may choose to set their web browser to refuse cookies, or to alert when cookies are being sent. 
                  If they do so, note that some parts of the Site may not function properly.
                </p>
                
                <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-600">
                  <p className="text-sm text-slate-400 mb-2">We use cookies for:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Authentication and session management</li>
                    <li>User preferences and settings</li>
                    <li>Site analytics and performance monitoring</li>
                    <li>Security and fraud prevention</li>
                  </ul>
                </div>

                <h3 className="text-lg font-semibold text-white">Third-Party Services</h3>
                <p>
                  We may use third-party services for authentication (such as Discord OAuth, Google OAuth), 
                  analytics, and other functionality. These services may collect information about your 
                  interactions with our Site according to their own privacy policies.
                </p>
              </CardContent>
            </Card>

            {/* Data Usage */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Eye className="h-6 w-6 text-purple-400" />
                  How We Use Your Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <p>Stellar Blade Mods may collect and use Users' personal information for the following purposes:</p>
                
                <div className="space-y-4">
                  <div className="border-l-4 border-blue-400 pl-4">
                    <h4 className="font-semibold text-blue-300">To run and operate our Site</h4>
                    <p className="text-sm">We may need your information to display content on the Site correctly and provide personalized experiences.</p>
                  </div>
                  
                  <div className="border-l-4 border-green-400 pl-4">
                    <h4 className="font-semibold text-green-300">To improve user experience</h4>
                    <p className="text-sm">We may use information in the aggregate to understand how our Users as a group use the services and resources provided on our Site.</p>
                  </div>
                  
                  <div className="border-l-4 border-yellow-400 pl-4">
                    <h4 className="font-semibold text-yellow-300">To improve our Site</h4>
                    <p className="text-sm">We may use feedback you provide to improve our products and services.</p>
                  </div>
                  
                  <div className="border-l-4 border-purple-400 pl-4">
                    <h4 className="font-semibold text-purple-300">To send periodic communications</h4>
                    <p className="text-sm">We may use the email address to send User information and updates. It may also be used to respond to inquiries, questions, and/or other requests.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Data Protection */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Shield className="h-6 w-6 text-green-400" />
                  How We Protect Your Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <p>
                  We adopt appropriate data collection, storage and processing practices and security measures 
                  to protect against unauthorized access, alteration, disclosure or destruction of your personal 
                  information, username, password, transaction information and data stored on our Site.
                </p>
                
                <div className="bg-green-900/20 border border-green-700/30 p-4 rounded-lg">
                  <h4 className="font-semibold text-green-300 mb-2">Security Measures</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Encrypted data transmission (HTTPS)</li>
                    <li>Secure authentication through trusted providers</li>
                    <li>Regular security audits and updates</li>
                    <li>Limited access to personal information</li>
                    <li>Data backup and recovery procedures</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Data Sharing */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Users className="h-6 w-6 text-red-400" />
                  Sharing Your Personal Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <p>
                  Except as described in this Privacy Policy, we will not share, sell, or rent individual 
                  personal information with anyone without your permission or unless ordered by a court of law.
                </p>
                
                <div className="bg-red-900/20 border border-red-700/30 p-4 rounded-lg">
                  <p className="text-sm text-red-300">
                    <strong>We do not sell your personal information to third parties.</strong> We may share 
                    generic aggregated demographic information not linked to any personal identification 
                    information regarding visitors and users with our business partners, trusted affiliates 
                    and advertisers for analytical purposes.
                  </p>
                </div>
                
                <h3 className="text-lg font-semibold text-white">Third Party Websites</h3>
                <p>
                  Users may find advertising or other content on our Site that link to the sites and services 
                  of our partners, suppliers, advertisers, sponsors, licensors and other third parties. We do 
                  not control the content or links that appear on these sites and are not responsible for the 
                  practices employed by websites linked to or from our Site.
                </p>
              </CardContent>
            </Card>

            {/* Children's Privacy */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Children's Online Privacy Protection</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <p>
                  Protecting the privacy of the very young is especially important. For that reason, we never 
                  collect or maintain information at our Site from those we actually know are under 13, and 
                  no part of our website is structured to attract anyone under 13.
                </p>
                
                <p>
                  If we learn that we have collected personal information from a child under 13, we will 
                  delete that information as quickly as possible. If you believe that we might have any 
                  information from a child under 13, please contact us.
                </p>
              </CardContent>
            </Card>

            {/* Policy Changes */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Changes to This Privacy Policy</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <p>
                  Stellar Blade Mods has the discretion to update this Privacy Policy at any time. When we do, 
                  we will post a notification on the main page of our Site and update the "last updated" date 
                  at the top of this policy. We encourage Users to frequently check this page for any changes 
                  to stay informed about how we are helping to protect the personal information we collect.
                </p>
                
                <p>
                  You acknowledge and agree that it is your responsibility to review this Privacy Policy 
                  periodically and become aware of modifications.
                </p>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Contact Us</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <p>
                  If you have any questions about this Privacy Policy, the practices of this site, or your 
                  dealings with this site, please contact us:
                </p>
                
                <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-600">
                  <div className="space-y-2 text-sm">
                    <p><strong>Email:</strong> <span className="text-blue-300">contact@stellarblademod.com</span></p>
                    <p><strong>Contact Page:</strong> <a href="/contact" className="text-blue-300 hover:text-blue-400 transition-colors">/contact</a></p>
                    <p><strong>Discord:</strong> <a href="https://discord.gg/daYR38qPB2" target="_blank" rel="noopener noreferrer" className="text-blue-300 hover:text-blue-400 transition-colors">Join our community</a></p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Acceptance */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Your Acceptance of These Terms</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <p>
                  By using this Site, you signify your acceptance of this policy. If you do not agree to this 
                  policy, please do not use our Site. Your continued use of the Site following the posting of 
                  changes to this policy will be deemed your acceptance of those changes.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
} 